import {Fragment, memo, useCallback, useMemo} from 'react';
import {translate} from '../../utils';
import {useAddGuestsStatic} from './AddGuestsContext';
import { clearFormikErrorsForGuestField, mapToOptions } from './addGuestsUtilities';
import { GuestLastnameField } from './GuestLastnameField';
import { GuestFirstnameField } from './GuestFirstnameField';
import { GuestBirthdayField } from './GuestBirthdayField';
import { GuestSelectField } from './GuestSelectField';

const LASTNAME_SUGGESTIONS_MENU_OPTIONS = { width: 'calc(150px * 2 - 24px)' };
const FIRSTNAME_SUGGESTIONS_MENU_OPTIONS = { anchor: 'right', width: 'calc(150px * 2 - 24px)' };

export const AddGuestsTableGuestEntry = memo(function AddGuestsTableGuestEntry({
    roomIndex,
    guestIndex,
    guest,
    guestErrors,
    titleOptions,
    countryOptions,
    languageOptions,
    vipOptions,
    nationalityOptions,
    regionMaps,
    onRegionMapsUpdate,
    errors,
    setErrors,
    setFieldValue,
    onRemoveGuest,
    onSelectClientSuggestion,
}) {
    const {guestColumnKeys} = useAddGuestsStatic();

    const regionOptions = useMemo(
        () => (guest.country && regionMaps[guest.country]) ? mapToOptions(regionMaps[guest.country]) : mapToOptions({}),
        [regionMaps, guest.country]
    );

    const selectOptionsMap = useMemo(() => ({
        clientTitleId: titleOptions,
        country: countryOptions,
        regionId: regionOptions,
        nationality: nationalityOptions,
        language: languageOptions,
        vipStatusId: vipOptions,
    }), [titleOptions, countryOptions, regionOptions, nationalityOptions, languageOptions, vipOptions]);

    const lastnameRelatedField = useMemo(
        () => ({ value: guest.firstname ?? '', param: 'first-name' }),
        [guest.firstname],
    );
    const firstnameRelatedField = useMemo(
        () => ({ value: guest.lastname ?? '', param: 'last-name' }),
        [guest.lastname],
    );

    const handleSelectClient = useCallback((selectedClient) => onSelectClientSuggestion(roomIndex, guestIndex, selectedClient), [onSelectClientSuggestion, roomIndex, guestIndex]);

    const handleGuestUpdate = useCallback((updatedRoomIndex, updatedGuestIndex, fieldKey, newValue) => {
        setFieldValue('rooms', (previousRooms) => {
            const nextRooms = buildUpdatedRooms(previousRooms, updatedRoomIndex, updatedGuestIndex, fieldKey, newValue);

            const nextErrors = clearFormikErrorsForGuestField(errors, previousRooms, updatedRoomIndex, updatedGuestIndex, fieldKey);
            if (nextErrors !== errors) {
                setErrors(nextErrors);
            }

            return nextRooms;
        }, false);
    }, [errors, setErrors, setFieldValue]);

    const guestRowFieldErrorMessages = useMemo(
        () => collectGuestRowFieldErrorMessages(guestErrors),
        [guestErrors],
    );

    const renderCell = (guestColumnKey) => {
        const fieldName = `rooms.${roomIndex}.guests.${guestIndex}.${guestColumnKey}`;

        if (guestColumnKey === 'lastname') {
            return (
                <GuestLastnameField
                    fieldName={fieldName}
                    roomIndex={roomIndex}
                    guestIndex={guestIndex}
                    guestLastname={guest.lastname ?? ''}
                    lastnameRelatedField={lastnameRelatedField}
                    lastnameSuggestionsMenuOptions={LASTNAME_SUGGESTIONS_MENU_OPTIONS}
                    onSelectClient={handleSelectClient}
                    onGuestUpdate={handleGuestUpdate}
                />
            );
        }

        if (guestColumnKey === 'firstname') {
            return (
                <GuestFirstnameField
                    fieldName={fieldName}
                    roomIndex={roomIndex}
                    guestIndex={guestIndex}
                    guestFirstname={guest.firstname ?? ''}
                    firstnameRelatedField={firstnameRelatedField}
                    firstnameSuggestionsMenuOptions={FIRSTNAME_SUGGESTIONS_MENU_OPTIONS}
                    onSelectClient={handleSelectClient}
                    onGuestUpdate={handleGuestUpdate}
                />
            );
        }

        if (guestColumnKey === 'birthday') {
            return (
                <GuestBirthdayField
                    fieldName={fieldName}
                    roomIndex={roomIndex}
                    guestIndex={guestIndex}
                    guestBirthday={guest.birthday ?? ''}
                    onGuestUpdate={handleGuestUpdate}
                />
            );
        }

        if (guestColumnKey in selectOptionsMap) {
            return (
                <GuestSelectField
                    fieldName={fieldName}
                    roomIndex={roomIndex}
                    guestIndex={guestIndex}
                    options={selectOptionsMap[guestColumnKey]}
                    guestColumnKey={guestColumnKey}
                    selectValue={guest[guestColumnKey] ?? ''}
                    onRegionMapsUpdate={onRegionMapsUpdate}
                    regionMaps={regionMaps}
                    onGuestUpdate={handleGuestUpdate}
                />
            );
        }

        return (
            <input
                name={fieldName}
                value={guest[guestColumnKey] ?? ''}
                type={guestColumnKey === 'email' ? 'email' : 'text'}
                className="form-control input-sm"
                onChange={(event) => handleGuestUpdate(roomIndex, guestIndex, guestColumnKey, event.target.value)}
            />
        );
    };

    return (
        <Fragment>
            {guestColumnKeys.map((guestColumnKey) => {
                const hasDuplicateClientError = Boolean(guestErrors?.clientKey);
                const columnHasError = hasDuplicateClientError || Boolean(guestErrors?.[guestColumnKey]);

                return (
                    <td key={guestColumnKey} className="vertical-align-top" data-column-key={guestColumnKey}>
                        <div className={['form-group', columnHasError ? 'has-error' : ''].filter(Boolean).join(' ')}>
                            {renderCell(guestColumnKey)}

                            {/* Show all errors for this guest row under the client title field instead of under each relevant field (solution for working with little space) */}
                            {guestColumnKey === 'clientTitleId' && guestRowFieldErrorMessages.length > 0 ? (
                                <div className="help-block">
                                    {guestRowFieldErrorMessages.map((message, messageIndex) => (
                                        <div key={messageIndex}>{message}</div>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    </td>
                );
            })}

            <td className="vertical-align-top skip-muted" data-column-key="guestActions">
                <button
                    type="button"
                    className="btn btn-default btn-sm"
                    title={translate('frontend', 'ADD_GUESTS_REMOVE_GUEST')}
                    onClick={() => onRemoveGuest(roomIndex, guestIndex)}
                >
                    <i className="fa fa-trash" />
                </button>
            </td>
        </Fragment>
    );
});

/**
 * @param {Array<{ guests: Array<Record<string, unknown>> }>} rooms
 * @param {number} updatedRoomIndex
 * @param {number} updatedGuestIndex
 * @param {string} fieldKey
 * @param {unknown} newValue
 * @returns {typeof rooms}
 */
function buildUpdatedRooms(rooms, updatedRoomIndex, updatedGuestIndex, fieldKey, newValue) {
    const updatedGuest = rooms[updatedRoomIndex].guests[updatedGuestIndex];
    const clientKey = updatedGuest.clientKey;

    if (fieldKey === 'clientKey' || clientKey === '') {
        return rooms.map((room, roomIndex) => {
            if (roomIndex !== updatedRoomIndex) return room;

            return {
                ...room,
                guests: room.guests.map((roomGuest, guestIndex) =>
                    guestIndex === updatedGuestIndex ? { ...roomGuest, [fieldKey]: newValue } : roomGuest
                ),
            };
        });
    }

    return rooms.map((room) => {
        const hasLinkedGuest = room.guests.some((roomGuest) => roomGuest.clientKey === clientKey);
        if (!hasLinkedGuest) {
            return room;
        }

        return {
            ...room,
            guests: room.guests.map((roomGuest) =>
                roomGuest.clientKey === clientKey ? { ...roomGuest, [fieldKey]: newValue } : roomGuest
            ),
        };
    });
}

/**
 * @param {Object<string, string> | undefined} guestErrors
 * @returns {string[]}
 */
function collectGuestRowFieldErrorMessages(guestErrors) {
    if (!guestErrors) return [];

    const guestErrorMessages = new Set();

    for (const guestErrorMessage of Object.values(guestErrors)) {
        if (guestErrorMessages.has(guestErrorMessage)) continue; // Skip duplicates, for example, for missing client firstname and lastname

        guestErrorMessages.add(guestErrorMessage);
    }

    return Array.from(guestErrorMessages);
}
